import * as signalR from '@microsoft/signalr';
import { API_ORIGIN } from '../utils/apiBase';

/**
 * City Portal real-time presence. Opens one SignalR connection to /hubs/square,
 * joins the current city's square, and pushes `presence` { count, initials }
 * updates as people pop in or leave. Degrades silently to the snapshot count if
 * the socket can't connect.
 */

let connection = null;
let connectPromise = null;
let currentSlug = null;
const handlers = new Set();
const eventHandlers = new Set(); // fired when new events land in the joined square

function hubUrl() {
  return `${API_ORIGIN}/hubs/square`;
}

async function ensureConnection() {
  if (connection && connection.state === signalR.HubConnectionState.Connected) return connection;
  if (connectPromise) return connectPromise;
  connectPromise = (async () => {
    if (!connection) {
      connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl(), {
          accessTokenFactory: () => localStorage.getItem('accessToken') || '',
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();
      connection.on('presence', (p) => {
        for (const h of handlers) { try { h(p); } catch { /* ignore */ } }
      });
      // New events were ingested for this city (e.g. Eventbrite/Ticketmaster just populated).
      connection.on('eventsUpdated', (info) => {
        for (const h of eventHandlers) { try { h(info); } catch { /* ignore */ } }
      });
      // Re-join the current square after a reconnect.
      connection.onreconnected(() => { if (currentSlug) connection.invoke('JoinSquare', currentSlug).catch(() => {}); });
    }
    try { await connection.start(); } catch { connectPromise = null; return null; }
    connectPromise = null;
    return connection;
  })();
  return connectPromise;
}

/** Join a city's square and receive live presence. Returns an async cleanup. */
export async function subscribePresence(slug, handler) {
  handlers.add(handler);
  const c = await ensureConnection();
  if (!c) return () => { handlers.delete(handler); };
  currentSlug = slug;
  try { await c.invoke('JoinSquare', slug); } catch { /* ignore */ }
  return async () => {
    handlers.delete(handler);
    try { if (connection && currentSlug) await connection.invoke('LeaveSquare', currentSlug); } catch { /* ignore */ }
    currentSlug = null;
  };
}

/** Subscribe to "new events landed in the joined square" pushes. Returns a cleanup fn.
 *  Assumes a square is (or will be) joined via subscribePresence. */
export function subscribeSquareEvents(handler) {
  eventHandlers.add(handler);
  ensureConnection();
  return () => { eventHandlers.delete(handler); };
}

export default { subscribePresence, subscribeSquareEvents };
